import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Layout,
  Card,
  Avatar,
  Button,
  Typography,
  Row,
  Col,
  Descriptions,
  Tag,
  Divider,
  Space,
  ConfigProvider,
  Spin,
  message,
  Upload
} from 'antd';
import {
  GlobalOutlined,
  MailOutlined,
  PhoneOutlined,
  UserOutlined,
  EditOutlined,
  SafetyCertificateOutlined,
  RocketOutlined,
  LoadingOutlined,
  CameraOutlined,
  TeamOutlined,
  CalendarOutlined,
  DollarOutlined,
  RiseOutlined
} from '@ant-design/icons';
import startupApi from '../../api/startupApi';
import EditStartupProfile from './EditStartupProfile';

const { Title, Text, Link } = Typography;
const { Content } = Layout;

const StartupProfile = () => {
  const navigate = useNavigate();
  const brandColor = '#3C007A';
  const [loading, setLoading] = useState(true);
  const [startupData, setStartupData] = useState(null);
  const [logoError, setLogoError] = useState(false);
  const [bannerError, setBannerError] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL || '';

  // Default fallback images
  const defaultLogo = 'https://ui-avatars.com/api/?name=Startup&background=3C007A&color=fff&size=128&bold=true';
  const defaultBanner = 'https://images.unsplash.com/photo-1559136555-9303baea8ebd?q=80&w=2070&auto=format&fit=crop';

  // Helper function to get image URL from server
  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith('http')) return imagePath;
    const normalizedPath = imagePath.replace(/\\/g, '/').replace(/^\/+/, '');
    if (normalizedPath.startsWith('uploads/')) {
      return `${API_URL}/${normalizedPath}`;
    }
    return `${API_URL}/uploads/${normalizedPath}`;
  };

  useEffect(() => {
    fetchStartupProfile();
  }, []);

  const fetchStartupProfile = async () => {
    try {
      setLoading(true);
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      
      if (userInfo.startup) {
        const data = await startupApi.getStartupProfile(userInfo.startup);
        setStartupData(data);
      } else {
        message.error('No startup ID found. Please login again.');
      }
    } catch (error) {
      message.error('Failed to load startup profile');
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (file) => {
    try {
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      const formData = new FormData();
      formData.append('logo', file);

      await startupApi.updateStartupProfile(userInfo.startup, formData);
      message.success('Logo uploaded successfully');
      fetchStartupProfile();
      return false;
    } catch (error) {
      message.error('Failed to upload logo');
      return false;
    }
  };

  const handleBannerUpload = async (file) => {
    try {
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      const formData = new FormData();
      formData.append('banner', file);

      await startupApi.updateStartupProfile(userInfo.startup, formData);
      message.success('Banner uploaded successfully');
      fetchStartupProfile();
      return false;
    } catch (error) {
      message.error('Failed to upload banner');
      return false;
    }
  };

  const getStatusColor = (status) => {
    const statusMap = {
      'verified': 'success',
      'pending': 'warning',
      'active': 'success',
      'approved': 'success',
      'inactive': 'default',
      'rejected': 'error'
    };
    return statusMap[status?.toLowerCase()] || 'default';
  };

  const getStatusText = (status) => {
    const statusTextMap = {
      'approved': 'Verified',
      'pending': 'Pending',
      'rejected': 'Rejected',
      'active': 'Active',
      'verified': 'Verified',
      'inactive': 'Inactive'
    };
    return statusTextMap[status?.toLowerCase()] || status || 'Pending';
  };

  const getStageColor = (stage) => {
    const stageColors = {
      'Idea': 'blue',
      'Prototype': 'cyan',
      'Seed': 'green',
      'Series A': 'orange',
      'Series B': 'red',
      'Series C+': 'purple'
    };
    return stageColors[stage] || 'default';
  };

  const handleEditProfile = () => {
    setEditModalVisible(true);
  };

  const handleEditSuccess = () => {
    fetchStartupProfile();
  };

  const logoUrl = startupData?.logo ? getImageUrl(startupData.logo) : defaultLogo;
  const bannerUrl = defaultBanner;

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: '#f0f2f5' }}>
        <Spin indicator={<LoadingOutlined style={{ fontSize: 48, color: brandColor }} spin />} />
      </div>
    );
  }

  if (!startupData) {
    return (
      <div style={{ textAlign: 'center', padding: '50px', backgroundColor: '#f0f2f5' }}>
        <Text type="secondary">No startup data available. Please contact support.</Text>
      </div>
    );
  }

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: brandColor,
          fontFamily: "'Rubik', sans-serif",
          borderRadius: 6,
          fontSize: 16,
        },
      }}
    >
      <div style={{
        width: '100%',
        minHeight: '100vh',
        backgroundColor: '#f0f2f5',
        position: 'relative',
        overflowX: 'hidden',
        fontFamily: "'Rubik', sans-serif"
      }}>
        {/* Banner Section */}
        <div style={{
          height: 'clamp(200px, 30vh, 320px)',
          background: `url(${bannerError ? defaultBanner : bannerUrl}) center center / cover no-repeat`,
          position: 'relative',
          width: '100%',
          maxWidth: '100%'
        }}>
          <div style={{
            position: 'absolute',
            top: 0, left: 0, right: 0, bottom: 0,
            background: 'linear-gradient(to bottom, rgba(60, 0, 122, 0.3), rgba(0,0,0,0.6))'
          }} />

          {/* Hidden image to check if banner loads */}
          <img 
            src={bannerUrl} 
            style={{ display: 'none' }}
            onError={() => {
              console.error('Banner failed to load:', bannerUrl);
              setBannerError(true);
            }}
            onLoad={() => console.log('Banner loaded successfully')}
            alt="banner"
          />

          {/* Upload Banner Button */}
          <Upload
            showUploadList={false}
            beforeUpload={handleBannerUpload}
            accept="image/*"
          >
            <Button
              icon={<CameraOutlined />}
              style={{ position: 'absolute', top: 20, right: 20, zIndex: 2 }}
            >
              Change Banner
            </Button>
          </Upload>

          {/* Edit Profile Button */}
          <Button
            icon={<EditOutlined />}
            type="primary"
            size="large"
            onClick={handleEditProfile}
            style={{
              position: 'absolute',
              bottom: 24,
              right: 24,
              zIndex: 10,
              fontSize: '16px',
              fontFamily: "'Rubik', sans-serif",
              fontWeight: 400,
              height: '44px',
              padding: '0 24px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              borderRadius: '8px',
              backgroundColor: brandColor,
              borderColor: brandColor,
              boxShadow: '0 4px 12px rgba(60, 0, 122, 0.3)',
            }}
          >
            Edit Profile
          </Button>
        </div>

        {/* Content Container */}
        <div style={{
          padding: '0 24px 24px',
          width: '100%',
          maxWidth: '1400px',
          margin: '0 auto',
          boxSizing: 'border-box'
        }}>
          {/* Header Card */}
          <Card
            bordered={false}
            style={{
              marginTop: -60,
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              position: 'relative',
              zIndex: 1,
              width: '100%',
              maxWidth: '100%'
            }}
            bodyStyle={{ padding: '24px 32px' }}
          >
            <Row gutter={[24, 24]} align="middle">
              <Col xs={24} sm={6} md={4} style={{ textAlign: 'center', position: 'relative' }}>
                <div style={{
                  position: 'relative',
                  display: 'inline-block'
                }}>
                  <div style={{
                    width: 140,
                    height: 140,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    position: 'relative'
                  }}>
                    {logoError || !startupData?.logo ? (
                      <Avatar
                        size={130}
                        style={{
                          backgroundColor: brandColor,
                          fontSize: 48,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white'
                        }}
                        icon={<RocketOutlined />}
                      >
                        {startupData?.startupName?.charAt(0)?.toUpperCase()}
                      </Avatar>
                    ) : (
                      <img
                        src={logoUrl}
                        alt="Startup Logo"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          padding: '8px'
                        }}
                        onError={() => {
                          console.error('Logo failed to load:', logoUrl);
                          setLogoError(true);
                        }}
                        onLoad={() => console.log('Logo loaded successfully')}
                      />
                    )}
                  </div>

                  {/* Upload Logo Button */}
                  <Upload
                    showUploadList={false}
                    beforeUpload={handleLogoUpload}
                    accept="image/*"
                  >
                    <Button
                      icon={<CameraOutlined />}
                      shape="circle"
                      style={{
                        position: 'absolute',
                        bottom: 5,
                        right: 5,
                        zIndex: 2,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                      }}
                    />
                  </Upload>
                </div>
              </Col>

              <Col xs={24} sm={18} md={20}>
                <Space direction="vertical" size={8}>
                  <Title
                    level={2}
                    style={{
                      margin: 0,
                      color: brandColor,
                      fontFamily: "'Rubik', sans-serif",
                      fontWeight: 500,
                      fontSize: '32px'
                    }}
                  >
                    {startupData.startupName || 'Startup Name'}
                  </Title>
                  <Space wrap>
                    <Tag
                      color="purple"
                      style={{
                        margin: 0,
                        fontSize: '16px',
                        padding: '4px 12px',
                        fontFamily: "'Rubik', sans-serif",
                        fontWeight: 400
                      }}
                    >
                      Startup
                    </Tag>
                    {startupData.stage && (
                      <Tag
                        icon={<RiseOutlined />}
                        color={getStageColor(startupData.stage)}
                        style={{
                          fontSize: '16px',
                          padding: '4px 12px',
                          fontFamily: "'Rubik', sans-serif",
                          fontWeight: 400
                        }}
                      >
                        {startupData.stage}
                      </Tag>
                    )}
                    {startupData.isVerified && (
                      <Tag
                        icon={<SafetyCertificateOutlined />}
                        color="success"
                        style={{
                          fontSize: '16px',
                          padding: '4px 12px',
                          fontFamily: "'Rubik', sans-serif",
                          fontWeight: 400
                        }}
                      >
                        Verified
                      </Tag>
                    )}
                  </Space>
                  {startupData.website && (
                    <Link
                      href={startupData.website}
                      target="_blank"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        fontSize: '16px',
                        fontFamily: "'Rubik', sans-serif",
                        fontWeight: 400,
                        color: "#333"
                      }}
                    >
                      <GlobalOutlined /> {startupData.website}
                    </Link>
                  )}
                </Space>
              </Col>
            </Row>
          </Card>

          {/* Details Grid */}
          <Row gutter={[24, 24]} style={{ marginTop: 24, width: '100%', maxWidth: '100%' }}>
            {/* Left Column: Startup Details */}
            <Col xs={24} lg={16}>
              <Card
                title={
                  <Space style={{ fontSize: '20px', fontFamily: "'Rubik', sans-serif", fontWeight: 400, color: brandColor }}>
                    <RocketOutlined style={{ color: brandColor, fontSize: '20px' }} />
                    Startup Details
                  </Space>
                }
                bordered={false}
                style={{ height: '100%' }}
              >
                <Descriptions
                  column={{ xs: 1, sm: 2 }}
                  layout="vertical"
                  labelStyle={{
                    fontWeight: 400,
                    color: '#666',
                    fontSize: '16px',
                    fontFamily: "'Rubik', sans-serif",
                    textAlign: 'center'
                  }}
                  contentStyle={{
                    fontSize: '17px',
                    fontFamily: "'Rubik', sans-serif",
                    fontWeight: 400,
                    color: brandColor
                  }}
                >
                  <Descriptions.Item label="Startup Name">
                    <Text strong style={{ fontSize: 18, fontFamily: "'Rubik', sans-serif", fontWeight: 400, color: brandColor }}>
                      {startupData.startupName || 'N/A'}
                    </Text>
                  </Descriptions.Item>

                  <Descriptions.Item label="Founder">
                    <Space>
                      <Text strong style={{ fontSize: 17, fontFamily: "'Rubik', sans-serif", fontWeight: 400 }}>
                        {startupData.founderName || 'N/A'}
                      </Text>
                    </Space>
                  </Descriptions.Item>

                  <Descriptions.Item label="Industry">
                    {startupData.industry || 'Not Specified'}
                  </Descriptions.Item>

                  <Descriptions.Item label="Stage">
                    {startupData.stage ? (
                      <Tag color={getStageColor(startupData.stage)}>{startupData.stage}</Tag>
                    ) : 'N/A'}
                  </Descriptions.Item>

                  {startupData.fundingStage && (
                    <Descriptions.Item label="Funding Stage">
                      {startupData.fundingStage}
                    </Descriptions.Item>
                  )}

                  {startupData.teamSize && (
                    <Descriptions.Item label="Team Size">
                      {startupData.teamSize}
                    </Descriptions.Item>
                  )}

                  {startupData.foundedYear && (
                    <Descriptions.Item label="Founded">
                      {startupData.foundedYear}
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </Card>

              <Card
                title={
                  <Space style={{
                    fontSize: '20px',
                    fontFamily: "'Rubik', sans-serif",
                    fontWeight: 400,
                    color: brandColor
                  }}>
                    About
                  </Space>
                }
                bordered={false}
                style={{ marginTop: 24 }}
              >
                <Text
                  style={{
                    display: 'block',
                    lineHeight: 1.8,
                    fontSize: '16px',
                    fontFamily: "'Rubik', sans-serif",
                    fontWeight: 400,
                    color: '#333'
                  }}
                >
                  {startupData.description ||
                    `${startupData.startupName || 'This startup'} is an innovative company working to transform the industry.`}
                </Text>
              </Card>
            </Col>

            {/* Right Column: Contact Information */}
            <Col xs={24} lg={8}>
              <Card
                title={
                  <Space style={{
                    fontSize: '20px',
                    fontFamily: "'Rubik', sans-serif",
                    fontWeight: 400,
                    color: brandColor
                  }}>
                    <UserOutlined style={{ color: brandColor }} />
                    Contact Information
                  </Space>
                }
                bordered={false}
                style={{
                  height: '100%',
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                }}
                headStyle={{
                  borderBottom: 'none',
                  padding: '12px 12px 0 12px'
                }}
                bodyStyle={{
                  padding: '24px'
                }}
              >
                <Space direction="vertical" size={24} style={{ width: '100%' }}>
                  <Divider style={{ margin: '3px 0' }} />
                  
                  {/* Founder */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <div style={{
                      width: 48,
                      height: 48,
                      borderRadius: '12px',
                      backgroundColor: '#F5F5F5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <UserOutlined style={{ fontSize: '20px', color: brandColor }} />
                    </div>
                    <div>
                      <Text type="secondary" style={{ fontSize: 16, display: 'block', marginBottom: 4, fontWeight: 400, fontFamily: "'Rubik', sans-serif", color: '#666' }}>
                        Founder
                      </Text>
                      <Text style={{ fontSize: 17, color: '#333', fontFamily: "'Rubik', sans-serif", fontWeight: 500 }}>
                        {startupData.founderName || 'N/A'}
                      </Text>
                    </div>
                  </div>

                  {/* Email */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <div style={{
                      width: 48,
                      height: 48,
                      borderRadius: '12px',
                      backgroundColor: '#F5F5F5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <MailOutlined style={{ fontSize: '20px', color: brandColor }} />
                    </div>
                    <div style={{ textAlign: 'left' }}>
                      <Text type="secondary" style={{ fontSize: 16, display: 'block', marginBottom: 4, fontWeight: 400, fontFamily: "'Rubik', sans-serif", color: '#666', textAlign: 'left' }}>
                        Email Address
                      </Text>
                      <Text style={{ fontSize: 17, color: '#333', fontFamily: "'Rubik', sans-serif", fontWeight: 500, textAlign: 'left' }}>
                        {startupData.email || 'N/A'}
                      </Text>
                    </div>
                  </div>

                  {/* Phone */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <div style={{
                      width: 48,
                      height: 48,
                      borderRadius: '12px',
                      backgroundColor: '#F5F5F5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <PhoneOutlined style={{ fontSize: '20px', color: brandColor }} />
                    </div>
                    <div>
                      <Text type="secondary" style={{ fontSize: 16, display: 'block', marginBottom: 4, fontWeight: 400, fontFamily: "'Rubik', sans-serif", color: '#666' }}>
                        Mobile Number
                      </Text>
                      <Text style={{ fontSize: 17, color: '#333', fontFamily: "'Rubik', sans-serif", fontWeight: 500 }}>
                        {startupData.phone || 'N/A'}
                      </Text>
                    </div>
                  </div>
                </Space>
              </Card>
            </Col>
          </Row>
        </div>
      </div>

      {/* Edit Profile Modal */}
      <EditStartupProfile
        visible={editModalVisible}
        onClose={() => setEditModalVisible(false)}
        onSuccess={handleEditSuccess}
        startupData={startupData}
      />
    </ConfigProvider>
  );
};

export default StartupProfile;
